import { useState, useRef, useEffect, ChangeEvent, FC } from "react";
import { IoIosArrowDown } from "react-icons/io";

export interface OptionType {
  id: number;
  value: string;
  [key: string]: any;
}

interface SelectFilterProps {
  options: OptionType[];
  placeholder?: string;
  onSelect: (option: OptionType) => void;
  className?: string;
  select?: OptionType | null;
  disabled?: boolean;
}

export const SelectFilter: FC<SelectFilterProps> = ({
  options,
  placeholder = "Select...",
  onSelect,
  className,
  select,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>(
    select && typeof select.value === "string" ? select.value.toLowerCase() : ""
  );
  const [selected, setSelected] = useState<OptionType | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(
    (option) =>
      typeof option.value === "string" &&
      option.value.toLowerCase().includes(filter.toLowerCase())
  );

  const handleSelect = (option: OptionType) => {
    setSelected(option);
    setIsOpen(false);
    onSelect(option);
    setFilter(option.value);
  };

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e && e.target && typeof e.target.value === "string") {
      setFilter(e.target.value.toLowerCase());
      setIsOpen(true);
      setSelected(null);
    }
  };

  useEffect(() => {
    if (select && select.value) {
      const found = options.find((option) => option.value === select.value);
      setSelected(found || null);
      setFilter(select.value.toLowerCase());
    }
  }, [select, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  return (
    <div
      className={`tw-relative tw-w-full tw-font-sans ${className || ""}`}
      ref={wrapperRef}
    >
      <input
        type="text"
        value={selected ? selected.value : filter}
        onChange={handleFilterChange}
        onFocus={() => {
          setIsOpen(true);
          setFilter("");
        }}
        className={`tw-w-full tw-h-8 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-md tw-box-border tw-text-gray-700 hover:tw-bg-gray-100 tw-transition-colors tw-text-xs`}
        placeholder={placeholder}
        disabled={disabled}
      />
      <button
        type="button"
        className="tw-absolute tw-h-8 tw-top-1/2 tw-right-2 tw-transform -tw-translate-y-1/2 tw-px-2 tw-py-1 tw-text-xs"
        onClick={() => {
          setIsOpen((prev) => !prev);
          setFilter("");
        }}
        aria-label="Toggle options"
        disabled={disabled}
      >
        <IoIosArrowDown />
      </button>
      {isOpen && (
        <ul className="tw-text-sm tw-font-normal tw-absolute tw-top-full tw-left-0 tw-right-0 tw-bg-white tw-border tw-border-gray-300 tw-rounded-b tw-list-none tw-p-0 tw-m-0 tw-max-h-[200px] tw-overflow-y-auto tw-z-[100]">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => handleSelect(option)}
                className="tw-px-2 tw-py-0.25 tw-cursor-pointer hover:tw-bg-gray-100"
              >
                {option.value}
              </li>
            ))
          ) : (
            <li className="tw-px-2 tw-py-0.25 tw-text-gray-400 tw-italic">
              No results found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
